import cx from 'classnames';
import { useState } from 'react';

import type { CommentType } from '~/interfaces/comment';
import type { Subscription } from '~/interfaces/subscriptions';

import Comment from './Comment';

interface CommentRendererProps {
  comment: CommentType;
  postId: string;
  subscription?: Subscription;
  commentsLocked: boolean;
  initialCommentsFetched: boolean;
  isPremiumCommentsOnly?: boolean;
  optimisticallyDeleteComment: (commentId: string) => void;
}

const CommentAndSubsCommentsRenderer = ({
  comment,
  postId,
  subscription,
  commentsLocked,
  initialCommentsFetched,
  isPremiumCommentsOnly,
  optimisticallyDeleteComment,
}: CommentRendererProps) => {
  const [isOptimisticallyDeleted, setIsOptimisticallyDeleted] = useState(false);
  const [subComments, setSubComments] = useState<CommentType[]>(comment.sub_comments);
  const hasSubcomments = subComments?.length > 0;

  const handleOptimisticallyRenderNewComment = (newComment: CommentType) => {
    const previousSubComments = subComments || [];
    setSubComments([...previousSubComments, newComment]);
  };

  const handleDeleteTopLevelComment = () => {
    optimisticallyDeleteComment(comment.id);
  };

  const handleDeleteSubComment = (commentId: string) => {
    // When not top level comment, we need to find the comment in question and delete that commment so as to not
    // Render it or any of its subcomments optimistically
    const optimisticallyDeleteComment = {
      ...comment,
      deleted_by_commenter_at: new Date().toISOString(),
    } as CommentType;

    const updatedComments = subComments.map((c) => {
      if (c.id === commentId) {
        return optimisticallyDeleteComment;
      }
      return c;
    });

    setSubComments(updatedComments);
    setIsOptimisticallyDeleted(true);
  };

  const handleOptimisticallyDeleteComment = (commentId: string) => {
    const depthLevel = comment.depth_level;

    if (depthLevel === 1) {
      handleDeleteTopLevelComment();
    } else {
      handleDeleteSubComment(commentId);
    }
  };

  return (
    <div className="relative space-y-1" key={comment.id}>
      <div className="absolute left-[14px] top-[38px] z-10 h-[calc(100%-45px)] border-l" />
      <Comment
        comment={comment}
        postId={postId}
        subscription={subscription}
        isMaxDepthLevel={comment.depth_level >= comment.max_allowed_depth_level}
        commentsLocked={commentsLocked}
        showReplyButton={true}
        isOptimisticallyDeleted={isOptimisticallyDeleted}
        onReply={(newComment) => handleOptimisticallyRenderNewComment(newComment)}
        onDelete={() => handleOptimisticallyDeleteComment(comment.id)}
        isPremiumCommentsOnly={isPremiumCommentsOnly}
      />
      {hasSubcomments && !isOptimisticallyDeleted && (
        <div className="pl-8">
          <CommentsFeed
            initialCommentsFetched={initialCommentsFetched}
            comments={subComments}
            postId={postId}
            subscription={subscription}
            optimisticallyDeleteComment={handleOptimisticallyDeleteComment}
            commentsLocked={commentsLocked}
            isSubcomments={true}
            isPremiumCommentsOnly={isPremiumCommentsOnly}
          />
        </div>
      )}
    </div>
  );
};

interface Props {
  initialCommentsFetched: boolean;
  comments: CommentType[];
  postId: string;
  subscription?: Subscription;
  commentsLocked: boolean;
  isSubcomments?: boolean;
  isPremiumCommentsOnly?: boolean;
  optimisticallyDeleteComment: (commentId: string) => void;
}

const CommentsFeed = ({
  initialCommentsFetched,
  comments,
  postId,
  subscription,
  commentsLocked,
  isPremiumCommentsOnly = false,
  isSubcomments = false,
  optimisticallyDeleteComment,
}: Props) => {
  return (
    <div className={cx('flex flex-col', isSubcomments ? 'space-y-2' : 'space-y-4')}>
      {comments.map((comment: CommentType, _index: number) => {
        const isDeleted = Boolean(comment.deleted_by_commenter_at);

        if (isDeleted) {
          return null;
        }

        return (
          <CommentAndSubsCommentsRenderer
            key={comment.id}
            comment={comment}
            postId={postId}
            subscription={subscription}
            commentsLocked={commentsLocked}
            initialCommentsFetched={initialCommentsFetched}
            isPremiumCommentsOnly={isPremiumCommentsOnly}
            optimisticallyDeleteComment={optimisticallyDeleteComment}
          />
        );
      })}
    </div>
  );
};

export default CommentsFeed;
